import axios from "axios";
import universalCookie from "universal-cookie";
import { useNavigate } from 'react-router-dom';
axios.defaults.withCredentials = true

const cookie = new universalCookie('/');

export function setJwtCookie(token) {
    cookie.set("access_token", token.access, {
        httpOnly: false,
        secure: false,
        path: '/' 
    });
    cookie.set("refresh_token", token.refresh, {
        httpOnly: false,
        secure: false,
        path: '/' 
    });
    cookie.set("is_admin", token.is_admin, {
        httpOnly: false,
        secure: false,
        path: '/'
    });
    if (token.expiration) {
        const [hours, minutes, seconds] = token.expiration.split(":");
        const totalSeconds = parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseInt(seconds);
        const expirationTime = new Date(Date.now() + totalSeconds * 1000);
        cookie.set("expiration", expirationTime, {
            httpOnly: false,
            secure: false,
            path: '/' 
        });
    }
}

export function clearJwtCookie() {
    try {
        cookie.remove("access_token", { path: '/' });
        cookie.remove("refresh_token", { path: '/' });
        cookie.remove("expiration", { path: '/' });
        cookie.remove("is_admin", { path: '/' });
    }catch(error) {
        console.error(error);
    }
    
}

export function getAccessToken() {

    return cookie.get("access_token", { path: '/' });
}

export function getRefreshToken() {
    return cookie.get("refresh_token", { path: '/' });
}

export function getExpirationTime() {
    return cookie.get("expiration", { path: '/' });
}

export function setAccessToken(token) {
    cookie.set("access_token", token, {
        httpOnly: false,
        secure: true,
        path: '/',
    });
}

export function getIsAdmin() {
    return cookie.get("is_admin", { path: '/' });
}

export const checkAndRefreshToken = async () => {
    const access_token = getAccessToken();
    const expirationTime = getExpirationTime();
    const refresh_token = getRefreshToken();

    if (access_token && new Date(Date.now()) > new Date(expirationTime)) {
      const response = await fetch(global.config.server_url + `user/refresh_token/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh: refresh_token,
        }),
      });

       if (response.ok) {
        clearJwtCookie();
        const token = await response.json();
        setJwtCookie(token);
        axiosInstance.defaults.headers['Authorization'] = `Bearer ${token.access}`;
        return token.access;
      } else {
        clearJwtCookie();
        throw new Error('Failed to refresh token');
      }
    }
    return access_token;
  };
  
const loginAxiosInstance = axios.create({
    withCredentials: false,
});

const axiosInstance = axios.create({
    headers: {
        Authorization: `Bearer ${getAccessToken()}`,
    },
});
let isRefreshing = false;
let refreshSubscribers = [];
function subscribeTokenRefresh(cb) {
    refreshSubscribers.push(cb);
}

function onRefreshed(token) {
    refreshSubscribers.map((cb) => cb(token));
    refreshSubscribers = [];
}

axiosInstance.interceptors.request.use(
    async (config) => {
        const access_token = getAccessToken();
        const expirationTime = getExpirationTime();
        const refresh_token = getRefreshToken();

        // Token expired
        if (access_token && new Date() > new Date(expirationTime)) {
            if (isRefreshing) {
                // Wait for ongoing refresh to complete
                return new Promise((resolve) => {
                    subscribeTokenRefresh((newToken) => {
                        config.headers.Authorization = `Bearer ${newToken}`;
                        resolve(config);
                    });
                });
            }

            isRefreshing = true;

            try {
                const response = await fetch(global.config.server_url + `user/refresh_token/`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ refresh: refresh_token }),
                });

                if (!response.ok) throw new Error('Refresh failed');

                const token = await response.json();
                if (!token || !token.access || !token.refresh) throw new Error('Invalid token');

                clearJwtCookie();
                setJwtCookie(token);

                config.headers.Authorization = `Bearer ${token.access}`;
                axiosInstance.defaults.headers['Authorization'] = `Bearer ${token.access}`;

                onRefreshed(token.access); // Notify waiting requests
                return config;
            } catch (error) {
                clearJwtCookie();
                console.error('Token refresh error:', error.message);
                return config;
            } finally {
                isRefreshing = false;
            }
        }

        return config;
    },
    (error) => Promise.reject(error)
);



axiosInstance.interceptors.response.use(
    (response) => {
        // If the response is successful, just return it
        return response;
    },
    (error) => {
        
        if (error.response) {
            const { status, data } = error.response;
            if (status != 401 && data.hasOwnProperty('detail')) {
                let redirectUrl = `/error?status=${status}`;

                // Only add the message parameter if it exists in the response
                if (data && data.detail) {
                    redirectUrl += `&message=${encodeURIComponent(data.detail)}`;
                }

                window.location.href = redirectUrl;

            }
           
        } else {
            // If there's no response, it's likely a network error
            window.location.href = '/error?status=500';
        }

        return Promise.reject(error);
    }
);

const axiosInstanceWithFile = axios.create({
    headers: {
        Authorization: `Bearer ${getAccessToken()}`,
        'Content-Type': 'multipart/form-data'
    },
});

axiosInstanceWithFile.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response) {
            const { status, data } = error.response;
            if (status !== 401 && data.hasOwnProperty('detail')) {
                let redirectUrl = `/error?status=${status}`;
                if (data.detail) {
                    redirectUrl += `&message=${encodeURIComponent(data.detail)}`;
                }
                window.location.href = redirectUrl;
            }
        } else {
            window.location.href = '/error?status=500';
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
